// src/components/Header.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
// import { Menu, Transition } from '@headlessui/react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import {
  HomeIcon,
  DocumentTextIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon,
  WalletIcon,
  ArrowLeftOnRectangleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const Header = () => {
  const location = useLocation();
  const {
    account,
    isOasysNetwork,
    // chainId,
    connectWallet,
    disconnect,
    switchToOasysNetwork,
    isAdmin,
  } = useWeb3();
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // スクロール検出
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // アドレスを短縮
  const shortenAddress = address => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // ナビゲーションリンク
  const navLinks = [
    { name: 'ホーム', path: '/', icon: <HomeIcon className="h-5 w-5 mr-1" /> },
    { name: '提案', path: '/proposals', icon: <DocumentTextIcon className="h-5 w-5 mr-1" /> },
    {
      name: 'コイン',
      path: '/tokens',
      icon: (
        <svg
          className="h-5 w-5 mr-1"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 16V8M15 12H9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      name: '概要',
      path: '/about',
      icon: (
        <svg
          className="h-5 w-5 mr-1"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ];

  // アカウントが接続されている場合、プロフィールリンクを追加
  if (account) {
    navLinks.push({
      name: 'プロフィール',
      path: `/user/${account}`,
      icon: <UserCircleIcon className="h-5 w-5 mr-1" />,
    });
  }

  if (account && isAdmin) {
    navLinks.push({
      name: '管理者',
      path: '/admin',
      icon: (
        <svg
          className="h-5 w-5 mr-1"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    });
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-10 transition-all duration-200 ${
        isScrolled
          ? 'bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/30'
          : 'bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* ロゴとナビゲーション */}
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="text-xl font-bold">
                <span className="text-blue-600 dark:text-blue-400">YukichiCoin</span>
                <span className="text-gray-700 dark:text-gray-300">Voting</span>
              </Link>
            </div>

            {/* デスクトップナビゲーション */}
            <nav className="hidden sm:ml-6 sm:flex sm:space-x-4 items-center">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    location.pathname === link.path
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {link.icon}
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* ユーザーメニューとモバイルメニューボタン */}
          <div className="flex items-center">
            {/* ダークモード切り替えボタン */}
            <button
              onClick={toggleDarkMode}
              className="mr-3 p-2 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              aria-label={isDarkMode ? 'ライトモードに切り替え' : 'ダークモードに切り替え'}
            >
              {isDarkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>

            {/* ネットワーク警告 */}
            {account && !isOasysNetwork && (
              <button
                onClick={switchToOasysNetwork}
                className="flex items-center mr-2 px-3 py-1.5 bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100 text-sm rounded hover:bg-yellow-200 dark:hover:bg-yellow-700"
              >
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                OASYSに切替
              </button>
            )}

            {/* ウォレット接続ボタンまたはユーザーメニュー */}
            {account ? (
              <div className="relative">
                <button
                  onClick={disconnect}
                  className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800"
                >
                  <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 px-3 py-1.5 rounded-full flex items-center">
                    <UserCircleIcon className="h-5 w-5 mr-1" />
                    {shortenAddress(account)}
                  </div>
                </button>
              </div>
            ) : (
              <button
                onClick={connectWallet}
                className="flex items-center px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600"
              >
                <WalletIcon className="h-5 w-5 mr-1" />
                <span>ウォレット接続</span>
              </button>
            )}

            {/* モバイルメニューボタン */}
            <div className="flex items-center sm:hidden ml-2">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <span className="sr-only">メニューを開く</span>
                {isMobileMenuOpen ? (
                  <XMarkIcon className="block h-6 w-6" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* モバイルメニュー */}
      <div className={`sm:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
        <div className="pt-2 pb-3 space-y-1 bg-white dark:bg-gray-800">
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              className={`flex items-center px-3 py-2 text-base font-medium ${
                location.pathname === link.path
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.icon}
              {link.name}
            </Link>
          ))}
        </div>

        {account && (
          <div className="pt-4 pb-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                <UserCircleIcon className="h-10 w-10 text-gray-400 dark:text-gray-300" />
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-800 dark:text-white">
                  {shortenAddress(account)}
                </div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  OASYSアドレス
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <button
                onClick={() => {
                  disconnect();
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeftOnRectangleIcon className="h-6 w-6 mr-2 text-gray-500 dark:text-gray-400" />
                切断
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
